import { useState, type FormEvent } from 'react';
import { grantablePermissions, type Role } from '@arcnet/contracts';
import {
  useCreateTenantRole, useDeleteRole, useRoles, useUpdateRole,
} from '../../lib/api/hooks-roles.js';
import { Badge, Empty, ErrorBanner, SectionHead, TableSkeleton } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';
import { PermissionEditor } from './PermissionEditor.js';

/**
 * The roles people on this account can be given (docs/33): built-in ones to read, custom ones to
 * change. A custom role made here belongs to this account.
 */

const OWNER_LABEL: Record<Role['owner'], string> = {
  builtin: 'Built in', platform: 'Platform', reseller: 'Reseller', tenant: 'This account',
};

type Editing =
  | { mode: 'new'; from?: Role }
  | { mode: 'edit' | 'view'; role: Role };

export function RolesSection({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const roles = useRoles({ tenantId });
  const remove = useDeleteRole();
  const [editing, setEditing] = useState<Editing | null>(null);

  return (
    <div style={{ marginTop: 22 }}>
      <SectionHead title="Roles" sub="What each role lets people see and do."
        action={can('role:write') && !editing && (
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => setEditing({ mode: 'new' })}>
            <Icon name="plus" size={13} /> New role
          </button>
        )} />

      {roles.error && <ErrorBanner error={roles.error} />}
      {remove.error && <ErrorBanner error={remove.error} />}
      {editing && <RoleEditor tenantId={tenantId} editing={editing} onDone={() => setEditing(null)} />}

      {!editing && (
        <div className="arc-card">
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr><th>Role</th><th>Kind</th><th>Permissions</th><th>People</th><th /></tr>
              </thead>
              {roles.isLoading ? <TableSkeleton cols={5} /> : (
                <tbody>
                  {roles.data?.data.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <div className="arc-stack">
                          <span style={{ fontWeight: 550 }}>{r.name}</span>
                          {r.description && <span className="arc-subtle">{r.description}</span>}
                        </div>
                      </td>
                      <td><Badge tone={r.owner === 'builtin' ? 'muted' : 'info'}>{OWNER_LABEL[r.owner]}</Badge></td>
                      <td className="arc-muted">{r.permissions.length}</td>
                      <td className="arc-muted">{r.holders}</td>
                      <td className="arc-td-right">
                        <div className="arc-row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                            onClick={() => setEditing({ mode: r.editable && can('role:write') ? 'edit' : 'view', role: r })}>
                            {r.editable && can('role:write') ? 'Edit' : 'View'}
                          </button>
                          {can('role:write') && r.grantable && (
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                              onClick={() => setEditing({ mode: 'new', from: r })}>
                              Copy
                            </button>
                          )}
                          {r.editable && can('role:write') && r.holders === 0 && (
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                              onClick={() => { if (confirm(`Delete the ${r.name} role?`)) remove.mutate(r.id); }}>
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>
          {!roles.isLoading && (roles.data?.data.length ?? 0) === 0 && (
            <Empty icon="users" title="No roles" hint="Make one to give people exactly the access they need." />
          )}
        </div>
      )}
    </div>
  );
}

function RoleEditor({ tenantId, editing, onDone }: { tenantId: string; editing: Editing; onDone: () => void }) {
  const sessionPerms = useSession((s) => s.permissions);
  const level = useSession((s) => s.session?.scope.level ?? 'tenant');
  // What they may give: for staff that includes the client-only permissions (docs/33 §3.3).
  const held = grantablePermissions(sessionPerms, level);
  const create = useCreateTenantRole(tenantId);
  const update = useUpdateRole();
  const base = editing.mode === 'new' ? editing.from : editing.role;
  const readOnly = editing.mode === 'view';
  const [name, setName] = useState(editing.mode === 'new'
    ? (editing.from ? `${editing.from.name} (copy)` : '') : editing.role.name);
  const [description, setDescription] = useState(base?.description ?? '');
  // A copy keeps only what the person copying may give; the rest they could not save anyway.
  const [perms, setPerms] = useState<Set<string>>(
    new Set((base?.permissions ?? []).filter((k) => editing.mode !== 'new' || held.has(k))));

  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const body = { name: name.trim(), description: description.trim() || null, permissions: [...perms].sort() };
    if (editing.mode === 'new') create.mutate(body, { onSuccess: onDone });
    else if (editing.mode === 'edit') update.mutate({ id: editing.role.id, ...body }, { onSuccess: onDone });
  };

  return (
    <form className="arc-card arc-card-body" onSubmit={submit} style={{ marginBottom: 14 }}>
      {error && <ErrorBanner error={error} />}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 14 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="role-name">Name</label>
          <input id="role-name" className="arc-input" value={name} required maxLength={60}
            readOnly={readOnly} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="role-desc">Description</label>
          <input id="role-desc" className="arc-input" value={description} maxLength={200}
            readOnly={readOnly} onChange={(e) => setDescription(e.target.value)} />
        </div>
      </div>

      <PermissionEditor level="tenant" value={perms} onChange={setPerms} held={held} readOnly={readOnly} />

      <div className="arc-row" style={{ gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
        <button type="button" className="arc-btn" onClick={onDone}>{readOnly ? 'Close' : 'Cancel'}</button>
        {!readOnly && (
          <button type="submit" className="arc-btn arc-btn-primary" disabled={pending || !name.trim()}>
            {pending ? 'Saving…' : editing.mode === 'new' ? 'Create role' : 'Save'}
          </button>
        )}
      </div>
    </form>
  );
}
